import { bool, func, string } from 'prop-types';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import { PROJECT_VIEW } from '../constants';
import { loadCreatives as loadCreativesAction } from '../creative-assets/actions';
import {
  clearContentfulCreatives,
  fetchContentfulCreatives,
} from '../creative-assets/ContentfulCreatives/actions';
import { ProvideCategories } from '../generic/CategorySelect';
import { fetchProducts } from '../products/actions';
import ProjectCampaignEditor from '../project/ProjectCampaign/ProjectCampaignEditor.container';
import { campaignShape } from '../prop-types';
import { closeEditor, hasInitialized, initializeEditor } from '../utils/editor';
import { isDevelopment, isE2E } from '../utils/env';
import FEATURE_FLAGS, {
  useSafeFlag,
  useSafeUnleashContext,
} from '../utils/flags';
import {
  fetchCampaignLineCreatives,
  initializeExpansionStateOfAllBusinessesAndProducts as initializeExpansionStateAction,
  setupCFAndCCPLinks,
  setupCFLinks,
} from './actions';
import CampaignEditor from './CampaignEditor.container';
import { EDITOR_NAME } from './connect-editor';
import transformForRendering from './transform-for-rendering';

class UpdateCampaignImpl extends React.Component {
  async componentDidMount() {
    const {
      initialCampaign,
      requestCampaignLineCreatives,
      setupDownstreamLinks,
    } = this.props;
    const { value: products } = await this.props.fetchProducts();
    this.initializeEditor(initialCampaign, products);
    requestCampaignLineCreatives();
    setupDownstreamLinks();
  }

  componentWillUnmount() {
    this.props.onClose();
    this.props.onCloseContentfulCreatives();
  }

  initializeEditor(initialCampaign, products) {
    const {
      onInitialData,
      initializeExpansionState,
      loadCreatives,
      loadContentCreatives,
    } = this.props;
    const { campaign, campaignLinesById } = transformForRendering(
      initialCampaign,
      products,
    );

    // If this becomes asynchronous, please also delay the
    // `requestCampaignLineCreatives` call accordingly.
    onInitialData({
      campaign,
      campaignLinesById,
      campaignLineCreativesByLineId: {},
    });
    initializeExpansionState(campaign.campaignLines);
    loadCreatives({ opportunityId: initialCampaign.opportunity.id });
    if (!isDevelopment() && !isE2E()) {
      loadContentCreatives();
    }
  }

  render() {
    const { initialized, view, isEditing, initialCampaign } = this.props;
    if (!initialized) {
      return null;
    }
    return (
      <ProvideCategories>
        {view === PROJECT_VIEW ? (
          <ProjectCampaignEditor view={view} isEditing={isEditing} />
        ) : (
          <CampaignEditor initialCampaign={initialCampaign} />
        )}
      </ProvideCategories>
    );
  }
}

UpdateCampaignImpl.propTypes = {
  initialCampaign: campaignShape.isRequired,
  initialized: bool.isRequired,
  onInitialData: func.isRequired,
  onClose: func.isRequired,
  onCloseContentfulCreatives: func.isRequired,
  requestCampaignLineCreatives: func.isRequired,
  setupDownstreamLinks: func.isRequired,
  loadCreatives: func.isRequired,
  loadContentCreatives: func.isRequired,
  initializeExpansionState: func.isRequired,
  fetchProducts: func.isRequired,
  view: string,
  isEditing: bool,
};

UpdateCampaignImpl.defaultProps = {
  view: null,
  isEditing: false,
};

function UpdateCampaign(props) {
  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);
  const setUnleashContext = useSafeUnleashContext();
  const dispatch = useDispatch();
  const businessPartnerId = props.initialCampaign?.account?.businessPartnerId;

  useEffect(() => {
    if (setUnleashContext && businessPartnerId) {
      setUnleashContext({
        userId: String(businessPartnerId),
      });
    }
  }, [businessPartnerId, setUnleashContext]);

  const setupDownstreamLinks = isDraftCampaignEnabled
    ? setupCFLinks
    : setupCFAndCCPLinks;

  return (
    <ConnectedUpdateCampaign
      {...props}
      setupDownstreamLinks={() => dispatch(setupDownstreamLinks())}
    />
  );
}

UpdateCampaign.propTypes = {
  initialCampaign: campaignShape.isRequired,
};

export const ConnectedUpdateCampaign = connect(
  state => ({
    initialized: hasInitialized(state, EDITOR_NAME),
  }),
  {
    onInitialData: initializeEditor.bind(null, EDITOR_NAME),
    onClose: closeEditor.bind(null, EDITOR_NAME),
    onCloseContentfulCreatives: clearContentfulCreatives,
    requestCampaignLineCreatives: fetchCampaignLineCreatives,
    loadCreatives: loadCreativesAction,
    initializeExpansionState: initializeExpansionStateAction,
    fetchProducts,
    loadContentCreatives: fetchContentfulCreatives,
  },
)(UpdateCampaignImpl);

// Export the base component for testing purposes only
export { UpdateCampaignImpl };

export default UpdateCampaign;
